import React, { useEffect, useState, useMemo } from "react";
import { useSelector } from "react-redux";

import { isNonEmptyArray, isNonEmptyString } from "@cayuse/shared-react.utils.type-checks";

import FeedTable from "components/FeedTable";
import SPLink from "components/SPLink";
import StartNewAward from "components/StartNewAward";
import { AWARDS_BASE_ROUTE, COLUMN_HEADER_KEYS, MODIFICATION_DRAFT } from "globals/constants";
import { useComponentDidMount, useDictionary, useSelectedRows, useColumnHeaders, useActiveColumns, useFormAccesses } from "hooks";
import useSimplePagination from "hooks/useSimplePagination";
import { findAwards } from "store/actions/awardList";
import { awardListSelectors } from "store/selectors";
import { getKeysFromColumnHeaders } from "utils";

import styles from "./styles.css";


const ProjectAwardsView = ({ projectId }) => {
	const { pageLimit, pageOffset, setPageLimit, setPageNumber } = useSimplePagination();
	const [ rows, setRows ] = useState([]);
	const getRowKey = useMemo(() => row => row._item.id, []);
	const { selectedRows, isSelectAll, setRowSelection } = useSelectedRows({ rows, getRowKey });
	const componentDidMount = useComponentDidMount();
	const formAccesses = useFormAccesses("award");
	const dictionary = useDictionary("awards");

	const { items, totalItems, isFetching, isRefresh } = useSelector(awardListSelectors.content);
	const { columnHeaders: formColumnHeaders } = useColumnHeaders(COLUMN_HEADER_KEYS.AWARD);
	const { columns: activeColumns } = useActiveColumns("AWARD", formColumnHeaders);

	const hasItems = isNonEmptyArray(items);

	const requiredFieldKeys = useMemo(() => getKeysFromColumnHeaders(activeColumns), [ activeColumns ]);

	const onPageChange = pageNumber => setPageNumber(pageNumber);

	const onPageLimitChange = pageLimit => setPageLimit(pageLimit);

	useEffect(() => {
		const updatedRows = hasItems
			? items.map(item => {
				const isAwardNumberLinkable = isNonEmptyString(item.awardId) && isNonEmptyString(item.awardNumber);
				const linkedAwardNumber = isAwardNumberLinkable
					? <SPLink to={`${AWARDS_BASE_ROUTE}/${item.awardId}`}>{item.awardNumber}</SPLink>
					: "";

				(item.subRows ?? []).forEach(subRow => {
					if (subRow.modificationStatus === MODIFICATION_DRAFT) {
						subRow.awardNumber = <span>{subRow.awardNumber} <i>(Draft)</i></span>;
					}
				});

				return {
					_isSelected: selectedRows.has(item.id),
					_item: item,
					...item,
					awardNumber: linkedAwardNumber
				};
			})
			: [];

		setRows(updatedRows);
	}, [ items, selectedRows, hasItems ]);

	useEffect(() => {
		if (projectId && componentDidMount) {
			findAwards({
				pageLimit,
				pageOffset,
				searchTerms: { projectId },
				fieldKeys: requiredFieldKeys
			});
		}
	}, [
		componentDidMount,
		pageLimit,
		pageOffset,
		projectId,
		requiredFieldKeys
	]);

	return (
		<>
			{formAccesses.updateForm && <div className={styles.startNewButton}>
				<StartNewAward
					key="startNewSubaward"
					fromProject
					projectId={projectId}
				/>
			</div>}
			<FeedTable
				isLoading={isFetching}
				isRefresh={isRefresh}
				headers={activeColumns}
				rows={rows}
				totalItems={totalItems}
				pageLimit={pageLimit}
				pageOffset={pageOffset}
				onPageChange={onPageChange}
				onPageLimitChange={onPageLimitChange}
				onRowSelectionChange={({ row, isSelected }) => setRowSelection(row, isSelected)}
				isSelectable
				isExpandable
				isSelectAll={isSelectAll}
				accessibilityLabel={({ projectTitle, _item }) => (
				`${projectTitle}, ${dictionary.accessibilityLabelAwardNumber || "Award #"}${_item.awardNumber}`
				)}
				noResultsLabel={dictionary.noResults || "No Saved Awards"}
				captionText="Project Awards"
			/>
		</>
	);
};

export default ProjectAwardsView;
